// A QUOTE STARTED FROM A CUSTOMER PO: THE TWO THINGS THAT DRAFT NEEDS AND NO
// OTHER QUOTE DOES.
//
//   1. A WAY OUT. The draft is created the moment the PDF is chosen -- before
//      anybody has seen a single line of it -- so an abandoned attempt has to
//      be removable, or every change of mind leaves a blank draft in the list
//      for ever. delete_quote (17.0.1.36.0) is what makes that possible; this
//      is its only caller.
//
//   2. A CUSTOMER. The customer is printed on the PO and is not known until
//      the PO has been read, so this draft is the one quote in the system
//      that legitimately exists without one -- and it cannot be finalised
//      until it has one (17.0.1.35.0). That is stated here rather than
//      discovered by pressing Save.
//
// ⚠️ IT READS ITS FACTS FROM TWO CALLS THAT ALREADY EXISTED, AND CHANGES
// NOTHING ABOUT THE PO PANEL BELOW IT. listQuotePos returns the PO rows AND
// `quoteCustomerName` -- the backend's own definition of whether this quote
// has a customer -- and listQuoteDocuments returns the filename. So the PO
// panel (PoComparison) is untouched by this slice: it is not asked to report
// anything upward, and its rendering is not changed.
//
// ⚠️ WHY THE PICKER IS OFFERED ONLY ONCE THERE ARE LINES, AND THIS IS A
// BACKEND FACT RATHER THAN A UI PREFERENCE. save_quote accepts an empty
// `lines` list ONLY on a CREATE (17.0.1.34.0); an UPDATE with no lines is
// still refused. A lineless draft therefore CANNOT be given a customer --
// there is no request that would carry one. Offering a picker there would be
// a control that cannot do what it says, which is the dead-control pattern
// this app has removed twice. So the empty phase states the position and the
// remedy ("add the lines first"), and the picker appears with the lines.

import { useCallback, useEffect, useState } from "react";
import {
  listQuoteDocuments, listQuotePos, searchCustomers, searchProducts,
  deleteQuoteDocument, deleteQuote, listDiscountRules, poLinesToQuoteRows,
  type QuoteDocument, type QuotePo,
} from "@/services/api";
import {
  matchPoLines, partNumbersToLookUp, NO_MATCH_TEXT,
  type PoLineMatch,
} from "@/services/poLineMatch";
import type { Material } from "@/types";
import { customerSearchTerm, findDuplicateCustomer } from "@/services/customerDuplicate";
import { CustomerPicker } from "@/components/CustomerPicker";
import type { CatalogProduct, CustomerPartner, QuoteCustomer } from "@/types";
import {
  colors, semanticColors, radius, spacing, heights, typography,
} from "@/styles/tokens";

/** The one wording for the customerless draft, said once. */
export const CUSTOMER_NOT_CHOSEN = "Customer not chosen";

/** What the amber note says a customerless draft cannot do. Deliberately the
 *  backend's own consequence rather than a paraphrase of its message: the
 *  refusal itself ("Choose a customer before finalising.") is shown by the
 *  save banner if anyone presses Save, and saying it twice in two wordings is
 *  how two explanations of one rule start to drift. */
export const CUSTOMER_NOT_CHOSEN_WHY =
  "This quote cannot be finalised until a customer is chosen.";

export const DELETE_DRAFT_QUESTION = "Delete this empty draft?";

const AMBER: React.CSSProperties = {
  padding: spacing[12], borderRadius: radius.base,
  backgroundColor: colors.amber50, border: `1px solid ${colors.amber200}`,
  color: colors.amber800, fontSize: typography.fontSize.sm,
};

const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[12]}px`,
  backgroundColor: semanticColors.surface, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, cursor: "pointer", whiteSpace: "nowrap",
};

export function StartFromPoStrip({
  quoteId, lineCount, canWrite, onDeleted, onChooseCustomer, onAddLines, reloadKey = 0,
}: {
  quoteId: number;
  /** The lines ON SCREEN. Whether this draft is still empty is a fact about
   *  the quote, and the quote screen is what holds it. */
  lineCount: number;
  canWrite: boolean;
  /** Called after the draft and its PDF are really gone, so the screen can
   *  leave a page whose record no longer exists. */
  onDeleted: () => void;
  /** The customer the person confirmed. Handed UP rather than saved here, so
   *  it rides the quote screen's existing autosave -- one write path, and the
   *  same one that already knows about tokens, supersession and the finalised
   *  latch. */
  onChooseCustomer: (customer: QuoteCustomer) => void;
  /** The quote lines a confirmed PO produced. Handed UP for exactly the
   *  reason the customer is: the quote screen owns the rows and the save. */
  onAddLines: (rows: Material[]) => Promise<void> | void;
  /** Bumped by the quote screen when a PO is recorded or removed below. Every
   *  offer here rests on the quote's POs, and without a re-read "Add these
   *  lines" did not appear until the page was reloaded -- while "Cancel"
   *  still offered to delete a draft that now held a PO. */
  reloadKey?: number;
}) {
  const [documents, setDocuments] = useState<QuoteDocument[]>([]);
  const [pos, setPos] = useState<QuotePo[]>([]);
  const [quoteCustomer, setQuoteCustomer] = useState<string | null | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    const [docs, poList] = await Promise.all([
      listQuoteDocuments(quoteId), listQuotePos(quoteId),
    ]);
    if (!docs.ok || !poList.ok) {
      // ⚠️ THE STRIP DISAPPEARS RATHER THAN GUESSING. Every decision it makes
      // -- is this draft empty, does it have a customer, is there a PDF --
      // rests on these two reads. A strip rendered from a failed read would
      // offer to delete a draft whose contents it does not know.
      setLoaded(false);
      setLoadError((docs.error || poList.error) ?? "Could not read this quote.");
      return;
    }
    setDocuments(docs.documents ?? []);
    setPos(poList.pos ?? []);
    setQuoteCustomer(poList.quoteCustomerName);
    setLoaded(true);
  }, [quoteId]);

  useEffect(() => { void load(); }, [load]);

  // Re-read when the line count changes: an Add in the review panel below
  // creates the PO and the lines together, and this strip's whole shape
  // depends on both.
  useEffect(() => { void load(); }, [lineCount, reloadKey, load]);

  const hasCustomer = typeof quoteCustomer === "string" && quoteCustomer.trim() !== "";
  // ⚠️ "STILL EMPTY" IS DERIVED, NOT MARKED. There is no "started from PO"
  // flag on a quote and this slice adds no column for one. An empty draft
  // carrying a PDF and no PO IS the state, however it was reached -- and a
  // draft somebody made by hand and attached a PDF to wants exactly the same
  // two affordances, so deriving it is not a shortcut.
  const stillEmpty = lineCount === 0 && pos.length === 0 && documents.length > 0;

  if (!loaded) {
    return loadError === "" ? null : (
      <div data-testid="start-from-po-load-error" style={AMBER}>{loadError}</div>
    );
  }
  // ⚠️ THE THIRD REASON TO RENDER: a PO is recorded and the quote has no
  // lines, so the offer below is the only thing that gets them there.
  const canOfferLines = pos.length > 0 && lineCount === 0 && canWrite;
  if (!stillEmpty && hasCustomer && !canOfferLines) return null;

  return (
    <div
      data-testid="start-from-po-strip"
      style={{ display: "flex", flexDirection: "column", gap: spacing[8], marginBottom: spacing[12] }}
    >
      {stillEmpty && (
        <StartedFrom
          filename={documents[0]?.filename ?? ""}
          quoteId={quoteId}
          documents={documents}
          canWrite={canWrite}
          onDeleted={onDeleted}
        />
      )}

      {/* ⚠️ THE PO'S LINES HAVE NOT REACHED THE QUOTE YET. save_quote_po
          writes anixify.po and anixify.po.line and says in its own docstring
          that it "touches no quote line" -- so a confirmed PO leaves the
          quote itself empty until this offer is taken. */}
      {pos.length > 0 && lineCount === 0 && canWrite && (
        <AddPoLines
          po={pos[0]}
          onAddLines={onAddLines}
          onAdded={() => { void load(); }}
        />
      )}

      {!hasCustomer && (
        <CustomerNote
          quoteId={quoteId}
          poCustomerName={pos[0]?.customer_name ?? null}
          canPick={canWrite && lineCount > 0}
          onChooseCustomer={onChooseCustomer}
        />
      )}
    </div>
  );
}

/** "Started from <file>", and the way out. */
function StartedFrom({
  filename, quoteId, documents, canWrite, onDeleted,
}: {
  filename: string; quoteId: number; documents: QuoteDocument[];
  canWrite: boolean; onDeleted: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const remove = async () => {
    setBusy(true);
    setError("");

    // ⚠️ THE DOCUMENTS GO FIRST, THROUGH THEIR OWN ROUTE, AND THAT ORDER IS
    // NOT AN IMPLEMENTATION DETAIL. delete_quote REFUSES a quote that still
    // holds a document, deliberately: removing the paperwork has to go
    // through delete_quote_document, which is the route that writes the audit
    // event naming who removed it. A quote-delete that swept documents away
    // itself would destroy a customer's paperwork with nothing on record.
    for (const document of documents) {
      const gone = await deleteQuoteDocument(quoteId, document.id);
      if (!gone.ok) {
        setBusy(false);
        // ⚠️ NEVER REPORT A PARTIAL DELETE AS A DELETE. The draft is still
        // there, and so is at least this file; saying so is the only honest
        // answer, and the person is told what is left.
        setError(
          `${document.filename} could not be removed: ${gone.error} `
          + "The draft has been left as it is."
        );
        return;
      }
    }

    const gone = await deleteQuote(quoteId);
    setBusy(false);
    if (!gone.ok) {
      setError(
        `The draft could not be deleted: ${gone.error} `
        + (documents.length
            ? "Its attached file has already been removed."
            : "")
      );
      return;
    }
    onDeleted();
  };

  return (
    <div
      data-testid="started-from-po"
      style={{
        padding: spacing[12], borderRadius: radius.base,
        backgroundColor: semanticColors.panelBg,
        border: `1px solid ${semanticColors.border}`,
        display: "flex", flexDirection: "column", gap: spacing[8],
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: spacing[12], flexWrap: "wrap",
      }}>
        <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          Started from{" "}
          <span
            data-testid="started-from-po-filename"
            style={{ color: semanticColors.textPrimary, fontWeight: typography.fontWeight.medium }}
          >
            {filename}
          </span>
          . Read it with &ldquo;Extract from PDF&rdquo; below, check every line, then add
          them. Nothing is added until you confirm.
        </div>
        {canWrite && !asking && (
          <button
            data-testid="started-from-po-cancel"
            onClick={() => setAsking(true)}
            style={{ ...BUTTON, flexShrink: 0 }}
          >
            Cancel
          </button>
        )}
      </div>

      {asking && (
        <div
          data-testid="delete-draft-confirm"
          style={{
            padding: spacing[12], borderRadius: radius.base,
            backgroundColor: semanticColors.surface,
            border: `1px solid ${semanticColors.border}`,
            display: "flex", flexDirection: "column", gap: spacing[8],
          }}
        >
          <div style={{
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            color: semanticColors.textPrimary,
          }}>
            {DELETE_DRAFT_QUESTION}
          </div>
          <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            Delete removes this draft and {filename}. Keep leaves both, and you
            can read the PDF again whenever you come back to it.
          </div>
          <div style={{ display: "flex", gap: spacing[8] }}>
            <button
              data-testid="delete-draft-delete"
              onClick={() => void remove()}
              disabled={busy}
              style={{
                ...BUTTON,
                backgroundColor: busy ? semanticColors.surface : semanticColors.destructiveBg,
                borderColor: semanticColors.destructiveBorder,
                color: busy ? semanticColors.textDisabled : semanticColors.destructive,
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              {busy ? "Deleting…" : "Delete"}
            </button>
            <button
              data-testid="delete-draft-keep"
              onClick={() => { setAsking(false); setError(""); }}
              disabled={busy}
              style={BUTTON}
            >
              Keep
            </button>
          </div>
          {error !== "" && (
            <div
              data-testid="delete-draft-error"
              style={{
                padding: spacing[8], borderRadius: radius.base,
                backgroundColor: semanticColors.destructiveBg,
                border: `1px solid ${semanticColors.destructiveBorder}`,
                color: semanticColors.destructive, fontSize: typography.fontSize.sm,
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** THE PO'S LINES, AND THE OFFER TO PUT THEM ON THE QUOTE.
 *
 *  WARNING: SHOWN BEFORE IT ACTS. The match is previewed per line -- matched
 *  product, or the reason there is none -- because a person about to put
 *  twenty lines on a quote should see which ones this app recognised BEFORE
 *  pressing, not discover it in the grid afterwards.
 *
 *  WARNING: OFFERED ONLY WHILE THE QUOTE HAS NO LINES, which is what makes it
 *  impossible to add the same PO twice. The caller gates on lineCount === 0;
 *  after a successful add the quote has lines and this disappears. That is a
 *  stronger guarantee than a "done" flag, because it survives a reload and a
 *  second tab.
 *
 *  WARNING: IT CARRIES NO PRICE FROM THE PO. poLinesToQuoteRows is the one
 *  place that builds the rows and its suite pins that; the note below says so
 *  on screen too, because the person is looking at the customer's prices in
 *  the panel beneath while they press this. */
function AddPoLines({
  po, onAddLines, onAdded,
}: {
  po: QuotePo;
  onAddLines: (rows: Material[]) => Promise<void> | void;
  onAdded: () => void;
}) {
  const [preview, setPreview] = useState<PoLineMatch[] | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const lines = po.lines ?? [];

  const look = async () => {
    setBusy("Checking the part numbers...");
    setError("");
    // WARNING: ONE READ PER DISTINCT PART NUMBER, and the RAW text is
    // searched -- the endpoint matches what is stored. A PO is a handful of
    // lines, so this is a handful of reads; matchPoLines then refuses
    // everything that is not an exact normalised equal, which is what makes a
    // substring search safe to feed it.
    const wanted = partNumbersToLookUp(lines);
    const found: CatalogProduct[] = [];
    for (const part of wanted) {
      const result = await searchProducts(part, 50);
      if (!result.ok) {
        setBusy("");
        // WARNING: NO PARTIAL MATCH SET. A failed read would make real
        // products look absent, and every line built from it would arrive as
        // free-text -- which is indistinguishable, afterwards, from a part we
        // genuinely do not carry.
        setError(
          "The product master could not be read (" + (result.error ?? "")
          + "), so the lines were not checked. Nothing has been added."
        );
        return;
      }
      found.push(...(result.products ?? []));
    }
    setBusy("");
    setPreview(matchPoLines(lines, found));
  };

  const add = async () => {
    if (!preview) return;
    setBusy("Adding the lines...");
    setError("");
    // The rules are loaded at ADD time, so a rate an Admin changed a moment
    // ago is the one recorded -- the same reason addMaterialsToQuote takes its
    // rules from the caller rather than a cache.
    const rules = await listDiscountRules();
    const rows = poLinesToQuoteRows(preview, {
      startLineNo: 1,
      rules: rules.ok ? (rules.rules ?? []) : [],
      // WARNING: NO CUSTOMER YET, AND THAT IS THE ORDINARY CASE HERE. With no
      // partner there is no discount rule to resolve, so every line arrives
      // unpriced with the refusal recorded -- exactly what a line added
      // before a customer is chosen does today.
    });
    try {
      await onAddLines(rows);
    } catch (err) {
      setBusy("");
      setError("The lines were not saved: " + (err as Error).message);
      return;
    }
    setBusy("");
    onAdded();
  };

  const matched = preview?.filter((m) => m.product).length ?? 0;

  return (
    <div
      data-testid="add-po-lines"
      style={{
        padding: spacing[12], borderRadius: radius.base,
        backgroundColor: semanticColors.panelBg,
        border: "1px solid " + semanticColors.border,
        display: "flex", flexDirection: "column", gap: spacing[8],
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: spacing[12], flexWrap: "wrap",
      }}>
        <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          <span style={{ color: semanticColors.textPrimary, fontWeight: typography.fontWeight.medium }}>
            PO {po.po_reference}
          </span>
          {" "}is recorded with {lines.length} line{lines.length === 1 ? "" : "s"},
          and this quote has none yet. Prices come from our own pricing and
          discount rules, never from the PO.
        </div>
        {preview === null && (
          <button
            data-testid="add-po-lines-check"
            onClick={() => void look()}
            disabled={busy !== "" || lines.length === 0}
            style={{ ...BUTTON, flexShrink: 0 }}
          >
            {busy || "Add these lines to the quote"}
          </button>
        )}
      </div>

      {preview !== null && (
        <>
          <div
            data-testid="add-po-lines-preview"
            style={{
              display: "flex", flexDirection: "column", gap: spacing[4],
              fontSize: typography.fontSize.sm,
            }}
          >
            {preview.map((m, i) => (
              <div
                key={i}
                data-testid={"add-po-line-" + i}
                style={{ display: "flex", gap: spacing[8], flexWrap: "wrap" }}
              >
                <span style={{ fontFamily: "monospace", color: semanticColors.textPrimary }}>
                  {(m.line as { part_number?: string }).part_number || "(no part number)"}
                </span>
                {m.product ? (
                  <span data-testid={"add-po-line-" + i + "-matched"}
                        style={{ color: semanticColors.textSecondary }}>
                    &rarr; {m.product.description}
                  </span>
                ) : (
                  <span data-testid={"add-po-line-" + i + "-unmatched"}
                        style={{ color: colors.amber800 }}>
                    {m.reason ? NO_MATCH_TEXT[m.reason] : ""}
                  </span>
                )}
              </div>
            ))}
          </div>

          <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            {matched} of {preview.length} matched the product master. Every line
            is added; the rest as free-text. All of them arrive without a price
            &mdash; enter one on each line, then the engine prices it.
          </div>

          <div style={{ display: "flex", gap: spacing[8] }}>
            <button
              data-testid="add-po-lines-confirm"
              onClick={() => void add()}
              disabled={busy !== ""}
              style={{
                ...BUTTON,
                backgroundColor: busy ? semanticColors.surface : colors.indigo600,
                color: busy ? semanticColors.textDisabled : semanticColors.onAccent,
                border: "none", fontWeight: typography.fontWeight.medium,
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              {busy || "Add " + preview.length + " line" + (preview.length === 1 ? "" : "s")}
            </button>
            <button
              data-testid="add-po-lines-cancel"
              onClick={() => { setPreview(null); setError(""); }}
              disabled={busy !== ""}
              style={BUTTON}
            >
              Not now
            </button>
          </div>
        </>
      )}

      {error !== "" && (
        <div
          data-testid="add-po-lines-error"
          style={{
            padding: spacing[8], borderRadius: radius.base,
            backgroundColor: semanticColors.destructiveBg,
            border: "1px solid " + semanticColors.destructiveBorder,
            color: semanticColors.destructive, fontSize: typography.fontSize.sm,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/** The amber note, and the suggestion the PO makes possible. */
function CustomerNote({
  quoteId, poCustomerName, canPick, onChooseCustomer,
}: {
  quoteId: number;
  poCustomerName: string | null;
  canPick: boolean;
  onChooseCustomer: (customer: QuoteCustomer) => void;
}) {
  const [suggestion, setSuggestion] = useState<CustomerPartner | null>(null);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<QuoteCustomer | undefined>(undefined);
  // ⚠️ THE CHOICE THIS TAB HAS MADE, WHICH IS NOT YET THE STORED CUSTOMER.
  //
  // The note's own reading of "has a customer" is the BACKEND's
  // (quote_customer_name), and that is right -- it is the only answer that
  // decides whether a finalise will be refused. But it means that without
  // this, choosing a customer changed nothing on screen: the amber note went
  // on saying "Customer not chosen" until a save had landed and the panel had
  // been re-read, so the person could not tell whether their click had done
  // anything.
  //
  // So the choice is acknowledged HERE and described HONESTLY -- chosen, not
  // saved -- rather than by pretending the stored customer has changed.
  const [confirmed, setConfirmed] = useState<CustomerPartner | null>(null);

  const choose = (customer: QuoteCustomer) => {
    setConfirmed(customer.entity ?? null);
    setPicking(false);
    onChooseCustomer(customer);
  };

  // ⚠️ THE SUGGESTION USES THE LIVE DUPLICATE-WARNING MATCHING, REUSED RATHER
  // THAN RE-DERIVED: customerSearchTerm + findDuplicateCustomer, the same pair
  // the customer picker warns with, which in turn rest on sameCompany. One
  // implementation means the PO's "customer differs" warning, the picker's
  // duplicate warning and this suggestion cannot drift into disagreeing about
  // whether two printed names denote one company.
  useEffect(() => {
    let live = true;
    const name = (poCustomerName ?? "").trim();
    if (!canPick || name === "") { setSuggestion(null); return; }
    void (async () => {
      const term = customerSearchTerm(name);
      if (term === "") return;
      const found = await searchCustomers(term);
      if (!live || !found.ok) return;
      const match = findDuplicateCustomer(name, null, found.customers ?? []);
      setSuggestion(match?.existing ?? null);
    })();
    return () => { live = false; };
  }, [poCustomerName, canPick]);

  return (
    <div data-testid="customer-not-chosen" style={AMBER}>
      <div style={{ fontWeight: typography.fontWeight.medium }}>{CUSTOMER_NOT_CHOSEN}</div>
      <div style={{ marginTop: spacing[4] }}>
        {CUSTOMER_NOT_CHOSEN_WHY}
        {poCustomerName && (
          <>
            {" "}The PO is addressed to{" "}
            <span
              data-testid="customer-not-chosen-po-name"
              style={{ fontWeight: typography.fontWeight.medium }}
            >
              {poCustomerName}
            </span>
            .
          </>
        )}
        {/* ⚠️ THE REMEDY, WHEN THE REMEDY IS NOT THIS STRIP. A lineless draft
            cannot be given a customer at all -- save_quote refuses an update
            with no lines -- so saying "choose one" there would be advice the
            screen cannot take. */}
        {!canPick && (
          <span data-testid="customer-not-chosen-later">
            {" "}Add the lines first, then choose the customer here.
          </span>
        )}
      </div>

      {confirmed && (
        <div
          data-testid="customer-chosen"
          style={{ marginTop: spacing[8], fontWeight: typography.fontWeight.medium }}
        >
          {confirmed.name} will be used. ⚠️ It is not saved yet — save this
          quote to store it.
        </div>
      )}

      {canPick && !confirmed && (
        <div style={{ marginTop: spacing[8], display: "flex", flexDirection: "column", gap: spacing[8] }}>
          {/* ⚠️ A SUGGESTION, CONFIRMED, NEVER APPLIED SILENTLY. The PO prints
              a NAME; this app joins on a RECORD. Two real companies can read
              alike, so the match is offered and a person accepts it. */}
          {suggestion && !picking && (
            <div style={{ display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap" }}>
              <span data-testid="customer-suggestion">
                {suggestion.name} is already a customer.
              </span>
              <button
                data-testid="customer-suggestion-use"
                onClick={() => choose({ entity: suggestion })}
                style={BUTTON}
              >
                Use {suggestion.name}
              </button>
              <button
                data-testid="customer-suggestion-pick"
                onClick={() => setPicking(true)}
                style={BUTTON}
              >
                Choose a different one
              </button>
            </div>
          )}

          {!suggestion && !picking && (
            <button
              data-testid="customer-choose"
              onClick={() => setPicking(true)}
              style={{ ...BUTTON, alignSelf: "flex-start" }}
            >
              Choose a customer
            </button>
          )}

          {picking && (
            <div
              data-testid="customer-not-chosen-picker"
              style={{
                padding: spacing[12], borderRadius: radius.base,
                backgroundColor: semanticColors.surface,
                border: `1px solid ${semanticColors.border}`,
              }}
            >
              <CustomerPicker value={picked} onChange={setPicked} />
              <div style={{ display: "flex", gap: spacing[8], marginTop: spacing[8] }}>
                <button
                  data-testid="customer-not-chosen-confirm"
                  onClick={() => { if (picked?.entity?.id) choose(picked); }}
                  disabled={!picked?.entity?.id}
                  style={{
                    ...BUTTON,
                    backgroundColor: picked?.entity?.id ? colors.indigo600 : semanticColors.textDisabled,
                    // ⚠️ onAccent, NOT white. tokens.ts says so in writing on
                    // indigo600 itself: white on this accent is 1.6:1. Some
                    // older primary buttons in this app do use white and are
                    // wrong about it; this is not the slice that changes them,
                    // but it is not a defect worth copying either.
                    color: semanticColors.onAccent,
                    border: "none",
                    fontWeight: typography.fontWeight.medium,
                    cursor: picked?.entity?.id ? "pointer" : "not-allowed",
                  }}
                >
                  Use this customer
                </button>
                <button
                  data-testid="customer-not-chosen-cancel"
                  onClick={() => { setPicking(false); setPicked(undefined); }}
                  style={BUTTON}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* quoteId is carried for the testids' sake only -- every write this
          strip makes goes through a route that takes it explicitly. */}
      <span data-testid="customer-not-chosen-quote" hidden>{quoteId}</span>
    </div>
  );
}
